import axios from 'axios'
import { createStore, useStore } from './createStore'

/*
 * Theming is pure CSS custom properties, which is how the stylesheets already
 * worked -- the old MUI `createTheme` call read these same variables back out
 * of the document and mirrored them into a JS palette. With MUI gone the
 * mirror goes too, and the variables are the single source of truth.
 *
 * The palettes themselves now live in @iziplay/ui-kit's tokens.css, under
 * `:root` and `:root[data-theme='dark']`. This module used to carry them as a
 * JS object and write each value with setProperty on every switch; what is
 * left here is the part that is genuinely app state -- which mode is active,
 * the user's accent and font overrides, and the sunrise lookup behind `auto`
 * -- expressed to the stylesheet through the one `data-theme` attribute.
 */

export type ThemeName = 'light' | 'dark' | 'auto'

const DEFAULT_ACCENT = '#7e94f7'

const themeStore = createStore<ThemeName>((window.localStorage.theme as ThemeName) || 'light')

/* The palette actually painted right now: `auto` resolves to one of these. */
const resolvedStore = createStore<'light' | 'dark'>('light')

let autoTimer: ReturnType<typeof setTimeout> | null = null
let autoListenersBound = false

function paint(mode: 'light' | 'dark') {
  const { style } = document.documentElement

  // Only the user's own overrides are written inline; the rest of the palette
  // is whichever tokens.css block `data-theme` selects. Re-applying the accent
  // on every switch is deliberate -- an inline property outranks both blocks,
  // so a user colour survives going dark and back.
  style.setProperty('--purple', window.localStorage.getItem('color') || DEFAULT_ACCENT)

  const font = window.localStorage.getItem('font')
  if (font) style.setProperty('--fonts', `'${font}', sans-serif`)

  // Selects the palette, and (via `color-scheme` in tokens.css) tells the
  // browser which form controls and scrollbars to render.
  document.documentElement.dataset.theme = mode

  resolvedStore.set(mode)
}

function scheduleAuto() {
  if (autoTimer) {
    clearTimeout(autoTimer)
    autoTimer = null
  }

  /*
   * The hourly timer is suspended while the device sleeps, so also re-check
   * the sun whenever the app regains visibility or focus.
   */
  if (!autoListenersBound) {
    autoListenersBound = true
    const refresh = () => {
      if (!document.hidden && window.localStorage.getItem('theme') === 'auto') applyTheme('auto')
    }
    document.addEventListener('visibilitychange', refresh)
    window.addEventListener('focus', refresh)
  }

  axios
    .get('https://geoip.fruitice.fr/v3')
    .then(res => {
      const sunrise = new Date(res.data.astral.sunrise)
      const sunset = new Date(res.data.astral.sunset)
      const now = new Date()

      paint(now > sunrise && now < sunset ? 'light' : 'dark')
      autoTimer = setTimeout(() => applyTheme('auto'), 60 * 60 * 1000)
    })
    .catch(() => {
      // Sunrise lookup is best-effort; fall back to the OS preference.
      paint(window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    })
}

export function applyTheme(theme: ThemeName) {
  themeStore.set(theme)

  if (theme === 'auto') {
    scheduleAuto()
    return
  }

  if (autoTimer) {
    clearTimeout(autoTimer)
    autoTimer = null
  }
  paint(theme)
}

export function saveTheme(theme: ThemeName) {
  window.localStorage.setItem('theme', theme)
  applyTheme(theme)
}

export function changeInterfaceColor(color: string) {
  window.localStorage.setItem('color', color)
  document.documentElement.style.setProperty('--purple', color)
}

export function setFont(font: string) {
  window.localStorage.setItem('font', font)
  document.documentElement.style.setProperty('--fonts', `'${font}', sans-serif`)
}

export const getTheme = () => themeStore.get()
export const useTheme = () => useStore(themeStore)
export const useResolvedTheme = () => useStore(resolvedStore)
